import React, { useMemo } from 'react';
import { feature } from 'topojson-client';
import land110m from 'world-atlas/land-110m.json';
import { fmtFt } from '../utils/conditions';

// Compact strike mini-map — a small graphic companion for the Strike calendar
// that lives in the Forecast page header, beside the title. The world is split
// into TWO tight regional panels (West Pacific / Indo and East Pacific →
// Atlantic) so the strip stays short and wide enough for the header band.
// Static SVG paths (no canvas / DPR / resize bookkeeping); spots with an
// upcoming strike window glow. Click a dot to jump to that spot's card;
// hover for a native tooltip.

const land = feature(land110m, land110m.objects.land);

// Land drawn once in wrapped-longitude coordinates (x = lon + {-360,0,360},
// y = 90 - lat); each panel is just a viewBox crop of the same path.
const LAND_PATH = (() => {
  const ring = (r, off) => {
    let d = '';
    let prev = null;
    for (let i = 0; i < r.length; i += 1) {
      // Unwrap antimeridian jumps (e.g. Fiji at ±180°) so the ring stays
      // continuous instead of drawing a seam line across the whole map.
      let lon = r[i][0];
      if (prev != null) {
        while (lon - prev > 180) lon -= 360;
        while (prev - lon > 180) lon += 360;
      }
      prev = lon;
      d += `${i ? 'L' : 'M'}${(lon + off).toFixed(1)},${(90 - r[i][1]).toFixed(1)}`;
    }
    return `${d}Z`;
  };
  let d = '';
  for (const off of [-360, 0, 360]) {
    for (const f of land.features) {
      const g = f.geometry;
      if (!g) continue;
      if (g.type === 'Polygon') g.coordinates.forEach((r) => { d += ring(r, off); });
      else if (g.type === 'MultiPolygon') g.coordinates.forEach((p) => p.forEach((r) => { d += ring(r, off); }));
    }
  }
  return d;
})();

// Latitude bands hug the tracked breaks so each panel reads wide, not tall.
// East is listed first so overlap spots (Hawaii ~202°) group with the
// Americas rather than pinning to the west panel's edge.
const PANELS = [
  { key: 'east', label: 'E Pacific · Atlantic', lonMin: 193, lonMax: 368, latMin: -26, latMax: 50 },
  { key: 'west', label: 'Indo · W Pacific', lonMin: 85, lonMax: 192, latMin: -26, latMax: 15 },
];

function strikeColor(ft) {
  if (ft == null) return '#3aa0ff';
  if (ft < 5) return '#34b3d6';
  if (ft < 8) return '#3aa0ff';
  if (ft < 12) return '#5b8def';
  if (ft < 18) return '#9b6cf2';
  if (ft < 28) return '#e0517a';
  return '#e0734a';
}

function projectInto(panel, lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < panel.latMin || lat > panel.latMax) return null;
  let clon = lng;
  while (clon < panel.lonMin) clon += 360;
  while (clon > panel.lonMax) clon -= 360;
  if (clon < panel.lonMin || clon > panel.lonMax) return null;
  return { x: clon, y: 90 - lat };
}

function whenLabel(sec) {
  if (!sec) return '';
  const days = Math.round((sec - Date.now() / 1000) / 86400);
  return days <= 0 ? 'now' : days === 1 ? 'in 1 day' : `in ${days} days`;
}

export default function StrikeMiniMap({ spots, rows, onFocus }) {
  const strikeById = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => { if (r.best) m.set(r.spot.id, r); });
    return m;
  }, [rows]);

  const byPanel = useMemo(() => {
    const out = { west: [], east: [] };
    for (const spot of spots || []) {
      for (const panel of PANELS) {
        const p = projectInto(panel, spot.lat, spot.lng);
        if (!p) continue;
        out[panel.key].push({ spot, ...p, row: strikeById.get(spot.id) || null });
        break; // first matching panel wins (windows overlap slightly)
      }
    }
    // Strikes last so their glow paints on top of the plain dots.
    for (const k of Object.keys(out)) out[k].sort((a, b) => (a.row ? 1 : 0) - (b.row ? 1 : 0));
    return out;
  }, [spots, strikeById]);

  // Render west→east (geographic left→right); PANELS order is claim priority.
  const renderPanels = [...PANELS].sort((a, b) => a.lonMin - b.lonMin);

  return (
    <div className="cs-minimap" role="group" aria-label="Strike map">
      {renderPanels.map((panel) => {
        const w = panel.lonMax - panel.lonMin;
        const h = panel.latMax - panel.latMin;
        return (
          <svg
            key={panel.key}
            className="cs-minimap-svg"
            viewBox={`${panel.lonMin} ${90 - panel.latMax} ${w} ${h}`}
            preserveAspectRatio="xMidYMid slice"
            style={{ aspectRatio: `${w} / ${h}` }}
          >
            <path d={LAND_PATH} fill="#1b2735" stroke="rgba(150,170,190,0.35)" strokeWidth="0.25" strokeLinejoin="round" />
            {byPanel[panel.key].map((p) => {
              const striking = !!p.row;
              const col = striking ? strikeColor(p.row.best.peakFt) : '#9aa6b5';
              const glow = striking ? Math.max(3, Math.min(6.5, 2 + (p.row.best.peakFt || 0) * 0.18)) : 0;
              const title = striking
                ? `${p.spot.name} — ${fmtFt(p.row.best.peakFt)} face · ${whenLabel(p.row.best.startSec)}`
                : `${p.spot.name} — no strike window`;
              return (
                <g
                  key={p.spot.id}
                  className={`cs-minimap-dot ${striking ? 'strike' : ''}`}
                  transform={`translate(${p.x.toFixed(1)},${p.y.toFixed(1)})`}
                  onClick={() => onFocus?.(p.spot.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <title>{title}</title>
                  {striking && <circle r={glow} fill={col} opacity="0.22" />}
                  {striking && <circle r={glow * 0.55} fill={col} opacity="0.32" />}
                  <circle
                    r={striking ? 1.6 : 1}
                    fill={col}
                    stroke={striking ? '#fff' : 'rgba(255,255,255,0.45)'}
                    strokeWidth={striking ? 0.45 : 0.3}
                  />
                </g>
              );
            })}
          </svg>
        );
      })}
    </div>
  );
}
